import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';

/**
 * Query options for repository operations
 */
export interface QueryOptions {
  filters?: Record<string, any>;
  orderBy?: { column: string; ascending: boolean };
  limit?: number;
  offset?: number;
}

/**
 * Configuration for repository instances
 */
export interface RepositoryConfig {
  tableName: string;
  primaryKey?: string;
}

/**
 * Base Repository class providing CRUD operations for database entities
 * 
 * This abstract class implements the Repository pattern, providing a clean
 * abstraction layer between the application and the database.
 * 
 * Benefits:
 * - Single point of change for table access
 * - Consistent error handling
 * - Type-safe operations
 * - Easy to test (can mock repositories)
 * - Easy to switch underlying tables during consolidation
 * 
 * @template T The entity type this repository manages
 * 
 * @example
 * ```typescript
 * class CustomerRepository extends BaseRepository<Customer> {
 *   constructor() {
 *     super({ tableName: 'customers', primaryKey: 'id' });
 *   }
 * }
 * ```
 */
export abstract class BaseRepository<T> {
  protected client: SupabaseClient;
  protected config: RepositoryConfig;

  constructor(config: RepositoryConfig) {
    this.client = supabase;
    this.config = config;
  }

  /**
   * Find all entities matching the given options
   * 
   * @param options Query options including filters, ordering, pagination
   * @returns Promise resolving to array of entities
   * 
   * @example
   * ```typescript
   * const customers = await repository.findAll({
   *   filters: { active: true },
   *   orderBy: { column: 'created_at', ascending: false },
   *   limit: 50
   * });
   * ```
   */
  async findAll(options?: QueryOptions): Promise<T[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Apply filters
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    }

    // Apply pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(
        options.offset, 
        options.offset + (options.limit || 50) - 1
      );
    }

    const { data, error } = await query;
    
    if (error) {
      throw new RepositoryError(
        `Failed to fetch from ${this.config.tableName}`, 
        error
      );
    }

    return (data || []) as T[];
  }

  /**
   * Find a single entity by its primary key
   * 
   * @param id The primary key value
   * @returns Promise resolving to the entity or null if not found
   * 
   * @example
   * ```typescript
   * const customer = await repository.findById('123e4567-e89b-12d3-a456-426614174000');
   * if (customer) {
   *   console.log(customer.name);
   * }
   * ```
   */
  async findById(id: string): Promise<T | null> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq(this.config.primaryKey || 'id', id)
      .single();

    if (error) {
      // PGRST116 is "not found" error - return null instead of throwing
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new RepositoryError(
        `Failed to fetch by ID from ${this.config.tableName}`, 
        error
      );
    }

    return data as T;
  }

  /**
   * Create a new entity in the database
   * 
   * @param data Partial entity data to insert
   * @returns Promise resolving to the created entity
   * 
   * @example
   * ```typescript
   * const newCustomer = await repository.create({
   *   name: 'John Doe',
   *   email: 'john@example.com'
   * });
   * console.log(newCustomer.id); // Auto-generated ID
   * ```
   */
  async create(data: Partial<T>): Promise<T> {
    const { data: created, error } = await this.client
      .from(this.config.tableName)
      .insert(data)
      .select()
      .single();

    if (error) {
      throw new RepositoryError(
        `Failed to create in ${this.config.tableName}`, 
        error
      );
    }

    return created as T;
  }

  /**
   * Update an existing entity
   * 
   * @param id The primary key of the entity to update
   * @param data Partial entity data to update
   * @returns Promise resolving to the updated entity
   * 
   * @example
   * ```typescript
   * const updated = await repository.update('123', {
   *   email: 'newemail@example.com'
   * });
   * ```
   */
  async update(id: string, data: Partial<T>): Promise<T> {
    const { data: updated, error } = await this.client
      .from(this.config.tableName)
      .update(data)
      .eq(this.config.primaryKey || 'id', id)
      .select()
      .single();

    if (error) {
      throw new RepositoryError(
        `Failed to update in ${this.config.tableName}`, 
        error
      );
    }

    return updated as T;
  }

  /**
   * Delete an entity by its primary key
   * 
   * @param id The primary key of the entity to delete
   * @returns Promise resolving when deletion is complete
   * 
   * @example
   * ```typescript
   * await repository.delete('123');
   * console.log('Customer deleted');
   * ```
   */
  async delete(id: string): Promise<void> {
    const { error } = await this.client
      .from(this.config.tableName)
      .delete()
      .eq(this.config.primaryKey || 'id', id);

    if (error) {
      throw new RepositoryError(
        `Failed to delete from ${this.config.tableName}`, 
        error
      );
    }
  }

  /**
   * Count entities matching the given filters
   * 
   * @param filters Optional filters to apply
   * @returns Promise resolving to the count
   * 
   * @example
   * ```typescript
   * const activeCount = await repository.count({ active: true });
   * console.log(`${activeCount} active customers`);
   * ```
   */
  async count(filters?: Record<string, any>): Promise<number> {
    let query = this.client
      .from(this.config.tableName)
      .select('*', { count: 'exact', head: true });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });
    }

    const { count, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to count in ${this.config.tableName}`, 
        error
      );
    }

    return count || 0;
  }

  /**
   * Check if an entity with the given ID exists
   * 
   * @param id The primary key to check
   * @returns Promise resolving to true if exists, false otherwise
   * 
   * @example
   * ```typescript
   * if (await repository.exists('123')) {
   *   console.log('Customer exists');
   * }
   * ```
   */
  async exists(id: string): Promise<boolean> {
    const entity = await this.findById(id);
    return entity !== null;
  }

  /**
   * Get the table name this repository manages
   * Useful for debugging and logging
   */
  getTableName(): string {
    return this.config.tableName;
  }

  /**
   * Get the primary key column name
   */
  getPrimaryKey(): string {
    return this.config.primaryKey || 'id';
  }
}

/**
 * Custom error class for repository operations
 * Provides context about which repository and what operation failed
 */
export class RepositoryError extends Error {
  public readonly tableName?: string;
  public readonly originalError?: any;

  constructor(message: string, originalError?: any) {
    super(message);
    this.name = 'RepositoryError';
    this.originalError = originalError;

    // Extract table name from message if present
    const match = message.match(/from (\w+)/);
    if (match) {
      this.tableName = match[1];
    }

    // Maintain proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RepositoryError);
    }
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    if (this.originalError?.message) {
      return `Database error: ${this.originalError.message}`;
    }
    return this.message;
  }

  /**
   * Check if this is a specific type of database error
   */
  isConstraintViolation(): boolean {
    return this.originalError?.code?.startsWith('23');
  }

  isNotFound(): boolean {
    return this.originalError?.code === 'PGRST116';
  }

  isPermissionDenied(): boolean {
    return this.originalError?.code === 'PGRST301';
  }
}
